import mixpanel from 'mixpanel-browser';
import { Capacitor } from '@capacitor/core';
import { logger } from '@/lib/logger';

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN;

// Inicializar Mixpanel
if (MIXPANEL_TOKEN) {
  mixpanel.init(MIXPANEL_TOKEN, {
    debug: import.meta.env.DEV,
    track_pageview: true,
    persistence: 'localStorage'
  });
}

export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
  const enrichedProps = {
    ...properties,
    platform: Capacitor.getPlatform(),
    app_version: import.meta.env.VITE_APP_VERSION || '1.0.0',
    timestamp: new Date().toISOString()
  };

  // Log estruturado para desenvolvimento
  if (import.meta.env.DEV) {
    logger.debug('Analytics Event', { 
      event: eventName, 
      ...enrichedProps 
    });
  }

  // Enviar para Mixpanel
  if (MIXPANEL_TOKEN) {
    mixpanel.track(eventName, enrichedProps);
  }

  // Enviar para Sentry como breadcrumb
  if (typeof window !== 'undefined' && (window as any).Sentry) {
    (window as any).Sentry.addBreadcrumb({
      category: 'analytics',
      message: eventName,
      data: enrichedProps,
      level: 'info'
    });
  }
};

export const identifyUser = (userId: string, traits?: Record<string, any>) => {
  if (!MIXPANEL_TOKEN) return;
  
  mixpanel.identify(userId);
  if (traits) {
    mixpanel.people.set(traits);
  }
};

export const setUserProperties = (properties: Record<string, any>) => {
  if (!MIXPANEL_TOKEN) return;
  
  mixpanel.people.set(properties);
};

export const incrementUserProperty = (property: string, value: number = 1) => {
  if (!MIXPANEL_TOKEN) return;
  
  mixpanel.people.increment(property, value);
};

// Analytics de eventos principais do app
export const analytics = {
  // Autenticação
  signUp: (method: 'email' | 'google') => 
    trackEvent('Sign Up', { method }),
  
  signIn: (method: 'email' | 'google') => 
    trackEvent('Sign In', { method }),
  
  signOut: () => 
    trackEvent('Sign Out'),
  
  // Onboarding
  onboardingStarted: () => 
    trackEvent('Onboarding Started'),
  
  onboardingCompleted: (emotion: string, objectives: string[]) => 
    trackEvent('Onboarding Completed', { emotion, objectives }),
  
  onboardingSkipped: (step: number) => 
    trackEvent('Onboarding Skipped', { step }),
  
  // Corridas
  runStarted: (suggestedDuration?: number) => 
    trackEvent('Run Started', { suggested_duration: suggestedDuration }),
  
  runPaused: (duration: number, distance: number) => 
    trackEvent('Run Paused', { duration, distance }),
  
  runResumed: () => 
    trackEvent('Run Resumed'),
  
  runCompleted: (distance: number, duration: number, pace: number, calories: number) => 
    trackEvent('Run Completed', { distance, duration, pace, calories }),
  
  // Badges e Gamificação
  badgeUnlocked: (badgeCode: string, category: string) => 
    trackEvent('Badge Unlocked', { badge_code: badgeCode, category }),
  
  shirtProgressed: (fromLevel: string, toLevel: string) => 
    trackEvent('Shirt Progressed', { from_level: fromLevel, to_level: toLevel }),
  
  // SOS e Apoio
  sosTriggered: (emotion: string, location: string) => 
    trackEvent('SOS Triggered', { emotion, location }),
  
  sosResponseReceived: (responseTime: number) => 
    trackEvent('SOS Response Received', { response_time: responseTime }),
  
  // RunMatch
  runmatchDetected: (heartIndex: number, distance: number) => 
    trackEvent('RunMatch Detected', { heart_index: heartIndex, distance }),
  
  runmatchAccepted: (heartIndex: number) => 
    trackEvent('RunMatch Accepted', { heart_index: heartIndex }),
  
  runmatchIgnored: () => 
    trackEvent('RunMatch Ignored'),
  
  duoRunCompleted: (distance: number, duration: number) => 
    trackEvent('Duo Run Completed', { distance, duration }),
  
  // Comunidade
  postCreated: (emotion: string, hasCaption: boolean) => 
    trackEvent('Post Created', { emotion, has_caption: hasCaption }),
  
  postReacted: (reactionType: string) => 
    trackEvent('Post Reacted', { reaction_type: reactionType }),
  
  groupChatMessageSent: () => 
    trackEvent('Group Chat Message Sent'),
  
  // Eventos
  eventViewed: (eventId: string, category: string) => 
    trackEvent('Event Viewed', { event_id: eventId, category }),
  
  eventCheckin: (eventId: string, method: 'qr' | 'manual') => 
    trackEvent('Event Checkin', { event_id: eventId, method }),
  
  eventCreated: (category: string, maxParticipants: number) => 
    trackEvent('Event Created', { category, max_participants: maxParticipants }),
  
  // Mentoria
  mentorMatched: (matchScore: number) => 
    trackEvent('Mentor Matched', { match_score: matchScore }),
  
  mentorMessageSent: () => 
    trackEvent('Mentor Message Sent'),
  
  mentorCourseCompleted: (courseId: string) => 
    trackEvent('Mentor Course Completed', { course_id: courseId }),
  
  // Clube
  videoWatched: (videoId: string, category: string, duration: number) => 
    trackEvent('Video Watched', { video_id: videoId, category, duration }),
  
  videoSaved: (videoId: string) => 
    trackEvent('Video Saved', { video_id: videoId }),
  
  // Premium/Monetização
  subscriptionViewed: () => 
    trackEvent('Subscription Viewed'),
  
  subscriptionStarted: (plan: 'premium' | 'gold', billing: 'monthly' | 'yearly') => 
    trackEvent('Subscription Started', { plan, billing }),
  
  subscriptionCanceled: (plan: string, reason?: string) => 
    trackEvent('Subscription Canceled', { plan, reason }),
  
  // Perfil
  profileEdited: (fields: string[]) => 
    trackEvent('Profile Edited', { fields }),
  
  locationShared: () => 
    trackEvent('Location Shared'),
  
  // Engajamento
  appOpened: () => 
    trackEvent('App Opened'),
  
  pageViewed: (page: string) => 
    trackEvent('Page Viewed', { page }),
  
  featureDiscovered: (feature: string) => 
    trackEvent('Feature Discovered', { feature }),
  
  // GPS & Tracking Quality
  trackRunStarted: (data: { userId: string; location?: { lat: number; lng: number } }) =>
    trackEvent('Run Started Advanced', data),
  
  trackRunPaused: (data: { duration: number; distance: number }) =>
    trackEvent('Run Paused Advanced', data),
  
  trackRunResumed: (data: { duration: number; distance: number }) =>
    trackEvent('Run Resumed Advanced', data),
  
  trackRunEnded: (data: { duration: number; distance: number; pace: number; calories: number; totalPoints: number; metrics?: any }) =>
    trackEvent('Run Ended Advanced', data),
  
  trackGPSQuality: (metrics: { p95Error: number; gapPercentage: number; batteryDrain: number; reconnectionTime: number }) =>
    trackEvent('GPS Quality', metrics),

  // Enhanced Observability
  trackGPSError: (errorMeters: number, fixTimeMs: number) =>
    trackEvent('GPS Error', { error_meters: errorMeters, fix_time_ms: fixTimeMs }),

  trackBatteryDrain: (drainPercent: number, durationMinutes: number) =>
    trackEvent('Battery Drain', { drain_percent: drainPercent, duration_minutes: durationMinutes }),

  trackAPILatency: (endpoint: string, latencyMs: number, statusCode: number) =>
    trackEvent('API Latency', { endpoint, latency_ms: latencyMs, status_code: statusCode }),
};
